import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from './app/hooks';
import { setLanguage, selectLanguage } from './data/languageSlice';
import { selectToken, setUserProfile, clearAccount, checkTokenExpiration } from './data/accountSlice';
import { AppState } from './data/statusSlice';
import { getUserProfile } from './api/user';
import { agreeToAgreement } from './api/agreement';
import LoginWelcomePage from './components/LoginWelcomePage';
import LoadingScreen from './components/LoadingScreen';
import GameplayScreen from './games/components/GameplayScreen';
import { useWalletConnect } from './hooks/useWalletConnect';
import { Toaster } from 'react-hot-toast';
import './App.css';
import './i18n';

function App() {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const currentLanguage = useAppSelector(selectLanguage);
  const token = useAppSelector(selectToken);
  const { isConnected, isConnecting } = useWalletConnect();
  const [appState, setAppState] = useState<AppState>(AppState.Welcome);

  // 初始化语言设置
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'zh-TW';
    if (savedLanguage !== currentLanguage) {
      i18n.changeLanguage(savedLanguage);
      dispatch(setLanguage(savedLanguage));
    }
    document.documentElement.lang = savedLanguage;
    
    // Check token expiration on load
    dispatch(checkTokenExpiration());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 更新HTML lang属性
  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  // Fetch User Profile if Token Exists
  useEffect(() => {
    const fetchProfile = async () => {
      if (token && isConnected) {
        try {
          const profile = await getUserProfile();
          dispatch(setUserProfile(profile));
          
          // Check for pending agreement
          const pendingAgreement = localStorage.getItem('pending_agreement');
          if (pendingAgreement === 'all') {
            try {
              // Assuming backend supports 'all' or we need to handle it.
              // User instruction said: "request with this agreementId = all"
              console.log('[App] Syncing pending agreement with backend');
              await agreeToAgreement('all');
              localStorage.removeItem('pending_agreement');
              console.log('[App] Pending agreement synced');
            } catch (err) {
              console.error('[App] Failed to sync pending agreement', err);
            }
          }

          if (appState === AppState.Welcome) {
             setAppState(AppState.Loading);
          }
        } catch (error) {
          console.error('[App] Failed to fetch user profile', error);
          // If 401, client.ts interceptor handles clearAccount, 
          // which will clear token, triggering state update in next effect
        }
      }
    };
    
    fetchProfile();
  }, [token, isConnected, dispatch]);

  // 监听钱包连接状态进行路由跳转
  useEffect(() => {
    if (isConnecting) return; // 等待连接状态确定

    if (token) {
      // If we have a token, we are logged in.
      // Even if 'isConnected' is temporarily false (e.g. during refresh/reconnect),
      // we should trust the token until we are explicitly disconnected or token expires.
      // However, to fetch profile or play game, we generally need wallet connection.
      
      // But for "persistence", if we have token, we should probably stay in Loading/Playing 
      // or at least NOT go back to Welcome unless we really have to.
      
      if (isConnected) {
        if (appState === AppState.Welcome) {
           setAppState(AppState.Loading);
        }
      }
      // If token exists but !isConnected, we might be reconnecting. 
      // Do NOT force Welcome yet. Let useWalletConnect handle 'disconnected' event to clear token.
    } else {
      // No token.
      if (appState !== AppState.Welcome) {
         setAppState(AppState.Welcome);
      }
    }
  }, [isConnected, isConnecting, appState, token]);

  // 完成登录流程后进入游戏 (保留作为回调)
  const handleLoginComplete = () => {
    // 登录页面的回调现在主要是为了触发加载，但我们现在的逻辑是 App 统一管理
    // 如果 LoginWelcomePage 需要手动触发下一步，可以调用这个
    setAppState(AppState.Loading);
  };

  // 资源加载完成回调
  const handleAssetsLoaded = () => {
    setAppState(AppState.Playing);
  };

  return (
    <div className={`app font-${currentLanguage.replace('-', '-').toLowerCase()}`}>
      <Toaster 
        position="bottom-center"
        containerStyle={{
          zIndex: 99999,
          bottom: 100, // 稍微向上抬一点，避免被底部导航栏遮挡（如果有的话）
        }}
        toastOptions={{
          duration: 2000,
          style: {
            background: 'rgba(0, 0, 0, 0.8)',
            color: '#fff',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
          },
        }}
      />
      {appState === AppState.Welcome && (
        <LoginWelcomePage onComplete={handleLoginComplete} />
      )}

      {appState === AppState.Loading && (
        <LoadingScreen onComplete={handleAssetsLoaded} />
      )}

      {appState === AppState.Playing && <GameplayScreen />}
    </div>
  );
}

export default App;
